import { Component, OnInit, Input } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { RoleObj } from '../../classes/role-obj';
import { DataSource } from '@angular/cdk/table';
import { AuthorizatorService } from '../../services/authorizator.service';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { UpsertRoleComponent, DialogData } from './upsert-role/upsert-role.component';
import { DeleteRoleWarningComponent } from './delete-role-warning/delete-role-warning.component';
import { PermissionManagerComponent, PermissionData } from '../permission-manager/permission-manager.component';
import { ProgressbarService } from '../../services/progressbar.service';

export class RolesDatabase {
  dataChange: BehaviorSubject<RoleObj[]> = new BehaviorSubject<RoleObj[]>([]);
  get data(): RoleObj[] { return this.dataChange.value; }

  constructor() { }
}

export class RolesDataSource extends DataSource<any> {
  constructor(private rolesDatabase: RolesDatabase) {
    super();
  }

  connect(): Observable<RoleObj[]> {
    return this.rolesDatabase.dataChange;
  }

  disconnect() {}
}


@Component({
  selector: 'lib-roles',
  templateUrl: './roles.component.html',
  styleUrls: ['./roles.component.scss']
})
export class RolesComponent implements OnInit {
  @Input() rootFirebasePath: string;
  public displayedColumns = ['remove', 'edit', 'role', 'permissions'];
  private rolesDatabase = new RolesDatabase();
  public rolesDataSource: RolesDataSource | null;
  public roles: RoleObj[] = [];
  constructor(
    private authorizatorservice: AuthorizatorService, public dialog: MatDialog,
    private snackbar: MatSnackBar,
    private progressbarservice: ProgressbarService
  ) { }

  ngOnInit() {
    this.progressbarservice.showRolesWaitBar(true);
    this.authorizatorservice.getRoles(this.rootFirebasePath).then(roles => {
      if (roles) {
        this.roles = roles.sort((prev, curr) => {
          if (prev.data.name > curr.data.name) {
            return 1;
          }
          if (prev.data.name < curr.data.name) {
            return -1;
          }
          return 0;
        });
        this.rolesDataSource = new RolesDataSource(this.rolesDatabase);
        this.rolesDatabase.dataChange.next(roles);
        this.progressbarservice.showRolesWaitBar(false);
      }
    }).catch(error => {
      console.error(error);
    }).finally(() => {
      this.progressbarservice.showRolesWaitBar(false);
    });
  }

  addRole() {
    const dialogRef = this.dialog.open(UpsertRoleComponent, {
      width: '95%',
      maxWidth: '500px',
      data: { role: null, rootFirebasePath: this.rootFirebasePath } as DialogData
    });

    dialogRef.afterClosed().subscribe(roleAdded => {
      if (roleAdded) {
        this.roles.push(roleAdded);
        this.roles = this.roles.sort((prev, curr) => {
          if (prev.data.name > curr.data.name) {
            return 1;
          }
          if (prev.data.name < curr.data.name) {
            return -1;
          }
          return 0;
        });
        this.rolesDatabase.dataChange.next(this.roles);
        const config: MatSnackBarConfig = new MatSnackBarConfig();
        config.duration = 3000;
        config.panelClass = ['success-bar'];
        this.snackbar.open('Role added successfully', null, config);
      }
    });
  }

  editRole(inputRole: RoleObj) {
    const dialogRef = this.dialog.open(UpsertRoleComponent, {
      width: '95%',
      maxWidth: '500px',
      data: { role: inputRole, rootFirebasePath: this.rootFirebasePath } as DialogData
    });

    dialogRef.afterClosed().subscribe(roleUpdated => {
      if (roleUpdated) {
        const roleUpdatedIndex = this.roles.findIndex(role => role.id === roleUpdated.id);
        if (roleUpdatedIndex > -1) {
          this.roles[roleUpdatedIndex].data = roleUpdated.data;
          this.rolesDatabase.dataChange.next(this.roles);
        }
        const config: MatSnackBarConfig = new MatSnackBarConfig();
        config.duration = 3000;
        config.panelClass = ['success-bar'];
        this.snackbar.open('Role updated successfully', null, config);
      }
    });
  }

  removeRole(role: RoleObj) {
    const dialogRef = this.dialog.open(DeleteRoleWarningComponent, {
      width: '300px',
      data: role
    });

    dialogRef.afterClosed().subscribe(response => {
      if (response) {
        this.progressbarservice.showRolesWaitBar(true);
        this.authorizatorservice.removeRole(this.rootFirebasePath, role.id).then(() => {
          this.roles.splice(
            this.roles.findIndex(roleArr => roleArr.id === role.id), 1
          );
          this.rolesDatabase.dataChange.next(this.roles);
          const config: MatSnackBarConfig = new MatSnackBarConfig();
          config.duration = 3000;
          config.panelClass = ['success-bar'];
          this.snackbar.open('Role removed successfully', null, config);
          this.progressbarservice.showRolesWaitBar(false);
        }).catch(error => {
          const config: MatSnackBarConfig = new MatSnackBarConfig();
          config.duration = 3000;
          config.panelClass = ['error-bar'];
          this.snackbar.open(error.message, null, config);
        });
      }
    });
  }

  setPermissions(roleInput: RoleObj) {
    const dialogRef = this.dialog.open(PermissionManagerComponent, {
      width: '95%',
      maxWidth: '600px',
      data: { rootFirebasePath: this.rootFirebasePath, role: roleInput } as PermissionData
    });
  }
}
